import type * as React from "react"
import { Home, Key, MessageCircle, User } from "react-feather"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

export interface NavItem {
  id: string
  label: string
  icon: React.ReactNode
  href?: string
  onClick?: () => void
  badge?: number
  active?: boolean
}

export interface GlobalNavProps extends React.HTMLAttributes<HTMLElement> {
  items?: NavItem[]
  activeItemId?: string
  onItemClick?: (item: NavItem) => void
}

const defaultItems: NavItem[] = [
  {
    id: "home",
    label: "ホーム",
    icon: <Home width={16} height={16} className="txst-slate-400" />,
    href: "/",
    // active: true,
  },
  {
    id: "property",
    label: "物件管理",
    icon: <Key width={16} />,
    href: "/properties",
  },
  {
    id: "tickets",
    label: "案件管理",
    icon: <MessageCircle width={16} height={16} className="txst-late-400" />,
    href: "/tickets",
  },
  // {
  //   id: "notification",
  //   label: "通知",
  //   icon: <Bell width={16} height={16} className="txst-slate-400" />,
  //   href: "/notifications",
  //   badge: 18,
  // },
  {
    id: "profile",
    label: "マイページ",
    icon: <User width={16} height={16} className="txst-slate-400" />,
    href: "/profile",
  },
]

function GlobalNav({
  className,
  items = defaultItems,
  activeItemId,
  onItemClick,
  ...props
}: GlobalNavProps) {
  const handleItemClick = (item: NavItem) => {
    if (onItemClick) {
      onItemClick(item)
    }
    if (item.onClick) {
      item.onClick()
    }
  }

  const isActive = (item: NavItem) => {
    if (activeItemId) {
      return item.id === activeItemId
    }
    return item.active
  }

  return (
    <nav
      className={cn(
        "fixed bottom-0 left-0 z-10 flex w-full items-center justify-center bg-primary shadow-sm",
        className
      )}
      {...props}
    >
      {items.map((item) => {
        const active = isActive(item)
        const Component = item.href ? "a" : "button"

        return (
          <Component
            key={item.id}
            href={item.href}
            onClick={() => handleItemClick(item)}
            className={cn(
              "relative flex w-[70px] h-[70px] flex-col items-center justify-center gap-1.5 transition-colors",
              "hover:text-primary hover:bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
              "min-w-[62px]",
              "text-slate-400",
              active && "text-white pointer-events-none"
            )}
          >
            <div className="flex flex-col items-center gap-1.5">
              <span className={cn("flex items-center h-[24px] transition-colors")}>
                {item.icon}
              </span>
              <span className={cn("text-xs font-semibold transition-colors")}>{item.label}</span>
            </div>
            {item.badge && item.badge > 0 && (
              <div className="absolute right-1 top-1 z-10">
                <Badge
                  variant="default"
                  className="h-6 min-w-[24px] rounded-full bg-[#DCE5FF] px-1.5 text-[13px] font-bold text-[#354267] hover:bg-[#DCE5FF]"
                >
                  {item.badge}
                </Badge>
              </div>
            )}
          </Component>
        )
      })}
    </nav>
  )
}

GlobalNav.displayName = "GlobalNav"

export { GlobalNav }
